import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Grid, Card, Box, Button,
  Chip, CircularProgress, Alert, Tabs, Tab, TextField,
} from '@mui/material';
import {
  PlayCircle as PlayIcon, MenuBook as BookIcon, EmojiEvents as CertIcon,
  ArrowForward as ArrowIcon, AccessTime as TimeIcon, Person as PersonIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import api from '../axios';
import PublicHeader from '../components/PublicHeader';

const LIME = '#00F1A7';
const DARK = '#05080F';
const GRAY_LIGHT = '#0A141C';
const PURPLE = '#00F1A7';
const GREEN = '#00F1A7';

const API = process.env.REACT_APP_API_URL || 'https://api.conectachurch.com.br';

export default function MeusCursos() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cursos, setCursos] = useState([]);
  const [erro, setErro] = useState('');
  const [tab, setTab] = useState(0);
  const [tokenValido, setTokenValido] = useState(false);
  const [email, setEmail] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [buscou, setBuscou] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token');
        setLoading(false);
        return;
      }
      setTokenValido(true);
      carregarCursos(token);
    } catch {
      localStorage.removeItem('token');
      setLoading(false);
    }
  }, []);

  const carregarCursos = async (token) => {
    setLoading(true);
    setErro('');
    try {
      const res = await api.get('/cursos/aluno/cursos-inscritos', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCursos(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        setTokenValido(false);
        setErro('Sessão expirada. Faça login novamente.');
      } else {
        setErro(errorMsg(err) || 'Erro ao carregar cursos');
      }
    }
    setLoading(false);
  };

  const buscarPorEmail = async () => {
    if (!email.trim()) { setErro('Digite seu email'); return; }
    setBuscando(true);
    setErro('');
    try {
      const res = await fetch(`${API}/minhas-inscricoes?email=${encodeURIComponent(email.trim())}`);
      if (!res.ok) { setErro('Erro ao buscar cursos'); setCursos([]); setBuscou(true); setBuscando(false); return; }
      const data = await res.json();
      if (data.length === 0) setErro('Nenhum curso encontrado para este email.');
      setCursos(data);
      setBuscou(true);
      setTokenValido(true);
    } catch {
      setErro('Erro ao buscar cursos. Tente novamente.');
    }
    setBuscando(false);
  };

  const cursosEmAndamento = cursos.filter(c => c.status === 'andamento');
  const cursosConcluidos = cursos.filter(c => c.status === 'concluido');
  const cursosDisponiveis = cursos.filter(c => c.status === 'aberto');

  const displayed = tab === 0 ? cursosEmAndamento : tab === 1 ? cursosDisponiveis : cursosConcluidos;

  const getProgresso = (curso) => {
    if (curso.total_sessoes === 0) return 0;
    return Math.round((curso.sessoes_concluidas / curso.total_sessoes) * 100);
  };

  return (
    <>
      <PublicHeader activePage="Meus Cursos" />

      {/* ── Hero ──────────────────────────────────────────────── */}
      <Box sx={{
        background: 'linear-gradient(150deg, #0A1D26, #05080F)',
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 300, height: 300, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              px: 2,
              py: 0.5,
              mb: 3,
            }}>
              <BookIcon sx={{ color: '#fff', fontSize: 18 }} />
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem', letterSpacing: 1 }}>
                PORTAL DO ALUNO
              </Typography>
            </Box>

            <Typography variant="h1" sx={{
              fontWeight: 800,
              color: '#fff',
              fontSize: { xs: '2rem', md: '3rem' },
              mb: 2,
            }}>
              Seus cursos e<br />
              <Box component="span" sx={{ color: LIME }}>treinamentos.</Box>
            </Typography>

            <Typography sx={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: { xs: '1rem', md: '1.15rem' },
              maxWidth: 520,
              mx: 'auto',
              lineHeight: 1.7,
            }}>
              Acompanhe suas inscrições, assista às aulas, registre presença e baixe seus certificados.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: LIME, fontWeight: 800, fontSize: '2rem' }}>
                  {tokenValido ? cursosEmAndamento.length : '—'}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                  Em andamento
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: LIME, fontWeight: 800, fontSize: '2rem' }}>
                  {tokenValido ? cursosConcluidos.length : '—'}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                  Concluídos
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: LIME, fontWeight: 800, fontSize: '2rem' }}>
                  {tokenValido ? cursosDisponiveis.length : '—'}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                  Disponíveis
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Tabs & Cursos ─────────────────────────────────────── */}
      <Box sx={{ bgcolor: GRAY_LIGHT, minHeight: '80vh', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              mb: 4,
              '& .MuiTab-root': { fontWeight: 700, fontSize: '1rem' },
              '& .Mui-selected': { color: PURPLE },
              '& .MuiTabs-indicator': { bgcolor: PURPLE },
            }}
          >
            <Tab label={`Em Andamento (${cursosEmAndamento.length})`} />
            <Tab label={`Disponíveis (${cursosDisponiveis.length})`} />
            <Tab label={`Concluídos (${cursosConcluidos.length})`} />
          </Tabs>

          {!tokenValido ? (
            <Card sx={{ borderRadius: 3, p: { xs: 4, md: 6 }, maxWidth: 480, mx: 'auto' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: `${PURPLE}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                  <BookIcon sx={{ color: PURPLE, fontSize: 36 }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#E9F1EF', mb: 0.5 }}>
                  Acesse seus cursos
                </Typography>
                <Typography sx={{ color: '#93A7A3', mb: 3, lineHeight: 1.7 }}>
                  Digite seu email para ver seus cursos e materiais
                </Typography>

                <TextField
                  fullWidth
                  label="Seu email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && buscarPorEmail()}
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={buscarPorEmail}
                  disabled={buscando}
                  sx={{ bgcolor: PURPLE, color: '#fff', fontWeight: 700, py: 1.5, mb: 2 }}
                >
                  {buscando ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Buscar cursos'}
                </Button>

                <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
                  <Typography sx={{ color: '#999', fontSize: '0.85rem', mb: 1 }}>
                    Ou faça login com sua conta
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/login')}
                    endIcon={<ArrowIcon />}
                    sx={{ borderColor: PURPLE, color: PURPLE, fontWeight: 700, py: 1.2 }}
                  >
                    Fazer login
                  </Button>
                </Box>
              </Box>
            </Card>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: PURPLE }} />
            </Box>
          ) : erro ? (
            <Alert severity="error" sx={{ mb: 3 }}>{erro}</Alert>
          ) : displayed.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <BookIcon sx={{ fontSize: 64, color: 'rgba(0,0,0,0.2)', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#E9F1EF', mb: 1 }}>
                Nenhum curso aqui ainda
              </Typography>
              <Typography sx={{ color: '#93A7A3', mb: 3 }}>
                {tab === 0 ? 'Você não tem cursos em andamento.' : tab === 1 ? 'Não há cursos disponíveis no momento.' : 'Você ainda não concluiu nenhum curso.'}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/cursos')}
                sx={{ bgcolor: PURPLE, color: '#fff', fontWeight: 700 }}
              >
                Ver todos os cursos
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {displayed.map((curso) => {
                const progresso = getProgresso(curso);
                return (
                  <Grid item xs={12} md={6} key={curso.id}>
                    <Card sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: 'none',
                      border: `1px solid #e0e0e0`,
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 16px 40px rgba(139,92,246,0.15)` },
                    }}>
                      <Box sx={{
                        bgcolor: curso.status === 'concluido' ? GREEN : PURPLE,
                        p: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}>
                        <Box>
                          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>
                            {curso.titulo}
                          </Typography>
                          <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                            {curso.ministerio_nome}
                          </Typography>
                        </Box>
                        <Chip
                          label={curso.status === 'concluido' ? 'Concluído' : curso.status === 'andamento' ? 'Em Andamento' : 'Disponível'}
                          size="small"
                          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }}
                        />
                      </Box>

                      <Box sx={{ p: 3 }}>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TimeIcon sx={{ color: '#888', fontSize: 18 }} />
                              <Typography sx={{ color: '#555', fontSize: '0.85rem' }}>
                                {curso.total_sessoes} sessões
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon sx={{ color: '#888', fontSize: 18 }} />
                              <Typography sx={{ color: '#555', fontSize: '0.85rem' }}>
                                {curso.instrutor_nome || 'A definir'}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {curso.status !== 'aberto' && (
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography sx={{ fontWeight: 700, color: '#E9F1EF', fontSize: '0.85rem' }}>Progresso</Typography>
                              <Typography sx={{ color: PURPLE, fontWeight: 700, fontSize: '0.85rem' }}>{progresso}%</Typography>
                            </Box>
                            <Box sx={{ height: 8, bgcolor: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                              <Box sx={{
                                height: '100%',
                                width: `${progresso}%`,
                                bgcolor: curso.status === 'concluido' ? GREEN : PURPLE,
                                borderRadius: 4,
                                transition: 'width 0.5s ease',
                              }} />
                            </Box>
                            <Typography sx={{ color: '#888', fontSize: '0.75rem', mt: 0.5 }}>
                              {curso.sessoes_concluidas || 0} de {curso.total_sessoes} sessões concluídas
                            </Typography>
                          </Box>
                        )}

                        {curso.status === 'concluido' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: `${GREEN}15`, borderRadius: 2, p: 1.5, mb: 2 }}>
                            <CertIcon sx={{ color: GREEN, fontSize: 22 }} />
                            <Typography sx={{ color: GREEN, fontWeight: 700, fontSize: '0.85rem' }}>Certificado disponível</Typography>
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<PlayIcon />}
                            onClick={() => navigate(`/cursos/${curso.id}`)}
                            sx={{ bgcolor: PURPLE, color: '#fff', fontWeight: 700, '&:hover': { bgcolor: '#00C48C' } }}
                          >
                            {curso.status === 'concluido' ? 'Revisar' : curso.status === 'andamento' ? 'Continuar' : 'Ver curso'}
                          </Button>
                          {curso.status === 'concluido' && (
                            <Button
                              variant="outlined"
                              startIcon={<CertIcon />}
                              onClick={() => navigate(`/cursos/${curso.id}/certificado`)}
                              sx={{ borderColor: GREEN, color: GREEN, fontWeight: 700, '&:hover': { bgcolor: `${GREEN}10`, borderColor: GREEN } }}
                            >
                              Certificado
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Box>

      {/* ── Footer ────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#E9F1EF', py: 4, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', mb: 0.5 }}>
            © 2026 ConectaChurch — Gestão de Comunidades Eclesiais
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.75rem' }}>
            Desenvolvido com compromisso pastoral por{' '}
            <Box component="span" sx={{ color: LIME, fontWeight: 700 }}>SmyrnaCore</Box> | Sementes Digitais
          </Typography>
        </Container>
      </Box>
    </>
  );
}
